import React from "react";
import { useFormikContext, ErrorMessage } from "formik";
import { dspDropdownOptions } from "../../Data/dropdownOptions";
import { getSubgenreOptions } from "../../utils/selectUtils";
import MultiSelect from "../MultiSelect/MultiSelect";
import { DSPPlatform, DropdownOptions } from '../../types/dropdownTypes';
import { PitchFormValues } from "../Schema/pitchValidationSchema";
import { RequiredAsterisk } from "../../../components/common/RequiredAsterisk";
import { SimilarArtist } from "./SimilarArtist";
import InfoTooltip from "../../../components/common/InfoTooltip";


interface DspSpecificInfoProps {
  selectedDSPs: {
    spotify: boolean;
    amazon: boolean;
    deezer: boolean;
    TIDAL: boolean;
    audiomack: boolean;
    soundcloud: boolean;
    apple: boolean;
    youtube: boolean;
  };
}

const DspSpecificInfo: React.FC<DspSpecificInfoProps> = ({ selectedDSPs }) => {
  const { values, setFieldValue, errors, touched } = useFormikContext<PitchFormValues>();
  const isDspSelected = (dsp: keyof typeof selectedDSPs): boolean =>
    selectedDSPs[dsp];

  const isBothYouTubeAndSoundCloudSelected = () => {
    return isDspSelected("youtube") && isDspSelected("soundcloud");
  };

  // Helper function to render select options
  const renderOptions = (options: string[]) => {
    return options.map((option) => (
      <option key={option} value={option.toLowerCase().replace(/\s+/g, "-")}>
        {option}
      </option>
    ));
  };

  const getSubgenres = (dsp: DSPPlatform): string[] => {
    return getSubgenreOptions(dsp, values.mainGenre || []);
  };

  const handleFileInputChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    field: string
  ) => {
    const file = e.target.files?.[0];
    if (file) {
      setFieldValue(field, file);
    }
  };

  // Add YouTube dropdown options

  return (
    <section className="p-6 bg-white rounded-lg shadow-sm">
      <h2 className="mb-6 text-xl font-semibold text-gray-900">DSP-Specific Information</h2>

      {/* Checkboxes Row */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {isDspSelected("spotify") && (
          <label
            htmlFor="spotify-playlist"
            className="flex min-h-[72px] items-start p-4 bg-white rounded-xl border border-gray-300 shadow-sm hover:shadow-md transition-all cursor-pointer gap-3"
          >
            <input
              type="checkbox"
              id="spotify-playlist"
              name="pinToThisIsPlaylist"
              checked={values.pinToThisIsPlaylist || false}
              onChange={(e) => setFieldValue("pinToThisIsPlaylist", e.target.checked)}
              className="w-5 h-5 mt-0.5 text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
            />
            <span className="text-sm font-medium leading-snug text-gray-800">
              Pin to "This Is" Playlist on Spotify
            </span>
          </label>
        )}

        {isDspSelected("amazon") && (
          <label
            htmlFor="add-amazon-voice-intro"
            className="flex min-h-[72px] items-start p-4 bg-white rounded-xl border border-gray-300 shadow-sm hover:shadow-md transition-all cursor-pointer gap-3"
          >
            <input
              type="checkbox"
              id="add-amazon-voice-intro"
              name="addAmazonVoiceIntro"
              checked={values.addAmazonVoiceIntro || false}
              onChange={(e) => setFieldValue("addAmazonVoiceIntro", e.target.checked)}
              className="w-5 h-5 mt-0.5 text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
            />
            <span className="text-sm font-medium leading-snug text-gray-800">
              Add Amazon Voice Intro
            </span>
          </label>
        )}

        {isDspSelected("audiomack") && (
          <label
            htmlFor="audiomack-trending"
            className="flex min-h-[72px] items-start p-4 bg-white rounded-xl border border-gray-300 shadow-sm hover:shadow-md transition-all cursor-pointer gap-3"
          >
            <input
              type="checkbox"
              id="audiomack-trending"
              name="pitchToAudiomackTrending"
              checked={values.pitchToAudiomackTrending || false}
              onChange={(e) => setFieldValue("pitchToAudiomackTrending", e.target.checked)}
              className="w-5 h-5 mt-0.5 text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
            />
            <span className="text-sm font-medium leading-snug text-gray-800">
              Pitch to Audiomack Trending
            </span>
          </label>
        )}

        {isDspSelected("soundcloud") && (
          <label
            htmlFor="is-premier-program"
            className="flex min-h-[72px] items-center p-4 bg-white rounded-xl border border-gray-300 shadow-sm hover:shadow-md transition-all cursor-pointer gap-3"
          >
            <span className="flex items-start pt-1" style={{ minWidth: 24 }}>
              <input
                type="checkbox"
                id="is-premier-program"
                name="isPremierProgram"
                checked={values.isPremierProgram || false}
                onChange={(e) => setFieldValue("isPremierProgram", e.target.checked)}
                className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
              />
            </span>
            <span className="flex-1 text-sm font-medium leading-snug text-gray-800">
              Are you part of SoundCloud's Premier program?
            </span>
          </label>
        )}
      </div>


      {/* Subgenre selection for each platform */}
      <div>
        <div className="grid grid-cols-2 gap-4 mt-4">
          {isDspSelected("spotify") && (
            <div>
              <label
                className="block mb-2 text-sm font-medium text-gray-900"
                htmlFor="spotify-subgenre"
              >
                Spotify Subgenre <RequiredAsterisk />
              </label>
              <MultiSelect
                options={getSubgenres("spotify")}
                value={values.spotifySubgenre || []}
                onChange={(value) => setFieldValue("spotifySubgenre", value)}
                placeholder="Select up to 3 subgenres"
                maxSelections={3}
                required={true}
              />
              <ErrorMessage name="spotifySubgenre" component="div" className="text-sm text-red-500" />
            </div>
          )}

          {isDspSelected("amazon") && (
            <div>
              <label
                className="block mb-2 text-sm font-medium text-gray-700"
                htmlFor="amazon-subgenre"
              >
                Amazon Subgenre <RequiredAsterisk />
              </label>
              <MultiSelect
                options={getSubgenres("amazon")}
                value={values.amazonSubgenre || []}
                onChange={(value) => setFieldValue("amazonSubgenre", value)}
                placeholder="Select up to 3 subgenres"
                maxSelections={3}
                required={true}
              />
              <ErrorMessage name="amazonSubgenre" component="div" className="text-sm text-red-500" />
            </div>
          )}

          {isDspSelected("deezer") && (
            <div>
              <label
                className="block mb-2 text-sm font-medium text-gray-900"
                htmlFor="deezer-subgenre"
              >
                Deezer Subgenre <RequiredAsterisk />
              </label>
              <MultiSelect
                options={getSubgenres("deezer")}
                value={values.deezerSubgenre || []}
                onChange={(value) => setFieldValue("deezerSubgenre", value)}
                placeholder="Select up to 3 subgenres"
                maxSelections={3}
                required={true}
              />
              <ErrorMessage name="deezerSubgenre" component="div" className="text-sm text-red-500" />
            </div>
          )}

          {isDspSelected("audiomack") && (
            <div>
              <label
                className="block mb-2 text-sm font-medium text-gray-900"
                htmlFor="audiomack-subgenre"
              >
                Audiomack Subgenre  <RequiredAsterisk />
              </label>
              <MultiSelect
                options={getSubgenres("audiomack")}
                value={values.audiomackSubgenre || []}
                onChange={(value) => setFieldValue("audiomackSubgenre", value)}
                placeholder="Select up to 3 subgenres"
                maxSelections={3}
                required={true}
              />
              <ErrorMessage name="audiomackSubgenre" component="div" className="text-sm text-red-500" />
            </div>
          )}
          {isDspSelected("TIDAL") && (
            <div>
              <label
                className="block mt-4 mb-2 text-sm font-medium text-gray-900"
                htmlFor="TIDAL-subgenre"
              >
                TIDAL Subgenre  <RequiredAsterisk />
              </label>
              <MultiSelect
                options={getSubgenres("TIDAL")}
                value={values.TIDALSubgenre || []}
                onChange={(value) => setFieldValue("TIDALSubgenre", value)}
                placeholder="Select up to 3 subgenres"
                maxSelections={3}
                required={true}
              />
              <ErrorMessage name="TIDALSubgenre" component="div" className="text-sm text-red-500" />
            </div>
          )}

          {isDspSelected("soundcloud") && (
            <div>
              <label
                className="block mt-4 mb-2 text-sm font-medium text-gray-900"
                htmlFor="soundcloud-subgenre"
              >
                SoundCloud Subgenre   <RequiredAsterisk />
              </label>
              <MultiSelect
                options={getSubgenres("soundcloud")}
                value={values.soundCloudSubgenre || []}
                onChange={(value) => setFieldValue("soundCloudSubgenre", value)}
                placeholder="Select up to 3 subgenres"
                maxSelections={1}
                required={true}
              />
              <ErrorMessage name="soundCloudSubgenre" component="div" className="text-sm text-red-500" />
            </div>
          )}
        </div>


      </div>

      {/* Spotify Specific Fields */}
      {isDspSelected("spotify") && (
        <div className="mt-2">
          <div className="grid grid-cols-2 gap-x-4">
            <div className="flex flex-col mb-4">
              <label
                className="block mt-4 mb-2 text-sm font-medium text-gray-700"
                htmlFor="hometown"
              >
                Artist Hometown <RequiredAsterisk />
                <InfoTooltip content="Spotify   <br> 
Artist's place of origin, used for regional playlist consideration and cultural context.
"   />
              </label>
              <input
                type="text"
                id="hometown"
                name="artistHometown"
                placeholder="Enter Hometown"
                value={values.artistHometown || ""}
                onChange={(e) => setFieldValue("artistHometown", e.target.value)}
                className={`w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-700 ${errors.artistHometown && touched.artistHometown ? 'border-red-500' : 'border-gray-300'
                  }`}
              />
              <ErrorMessage name="artistHometown" component="div" className="text-sm text-red-500" />
            </div>
            <div className="flex flex-col mb-4">
              <label
                className="block mt-4 mb-2 text-sm font-medium text-gray-700"
                htmlFor="current-town"
              >
                Artist Current Town  <RequiredAsterisk />
                <InfoTooltip content={`Spotify <br> Where the artist currently resides, helping inform geographic targeting and local scene connections.`} />

              </label>
              <input
                type="text"
                id="current-town"
                name="artistCurrentTown"
                placeholder="Enter Current Town"
                value={values.artistCurrentTown || ""}
                onChange={(e) => setFieldValue("artistCurrentTown", e.target.value)}
                className={`w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-700 ${errors.artistCurrentTown && touched.artistCurrentTown ? 'border-red-500' : 'border-gray-300'
                  }`}
              />
              <ErrorMessage name="artistCurrentTown" component="div" className="text-sm text-red-500" />
            </div>

            <div className="mt-4">
              <label className="block mb-1 text-sm font-medium text-gray-700">
                Music Culture<RequiredAsterisk />
                <InfoTooltip content="Spotify <br> Select the cultural movement or scene this song represents. Choose none of these if no
options apply." />
              </label>
              <MultiSelect
                // label="Music Culture"
                options={dspDropdownOptions.musicCulture}
                value={values.musicCulture || []}
                onChange={(value) => setFieldValue("musicCulture", value)}
                placeholder="Select Music Culture"
                maxSelections={2}
                required={true}
              />
              <ErrorMessage name="musicCulture" component="div" className="text-sm text-red-500" />
            </div>

            <div className="mt-4">
              <label className="block mb-1 text-sm font-medium text-gray-700">
                Instruments<RequiredAsterisk />
                <InfoTooltip content="Spotify <br> Select all instruments featured prominently in the track for genre-specific and instrument-based
playlist placement."/>
              </label>
              <MultiSelect
                // label="Instruments"
                options={dspDropdownOptions.instruments}
                value={values.instruments || []}
                onChange={(value) => setFieldValue("instruments", value)}
                placeholder="Select Instruments"
                required={true}
              />
              <ErrorMessage name="instruments" component="div" className="text-sm text-red-500" />
            </div>
          </div>

          <div className="mt-4">
            <label className="block mb-1 text-sm font-medium text-gray-700">
              Song Style<RequiredAsterisk />
              <InfoTooltip content="Spotify <br> Choose the style that best describes this track's sound. Select none of these if no options
accurately represent the song.
"/>
            </label>
            <MultiSelect
              // label="Song Style"
              options={dspDropdownOptions.songStyle}
              value={values.songStyle || []}
              onChange={(value) => setFieldValue("songStyle", value)}
              placeholder="Select Style"
              maxSelections={3}
              required={true}
            />
            <ErrorMessage name="songStyle" component="div" className="text-sm text-red-500" />
          </div>

          <div className="flex flex-col mt-4 mb-4">
            <label
              className="block mb-2 text-sm font-medium text-gray-700"
              htmlFor="spotify-url"
            >
              Spotify URI  <RequiredAsterisk />
              <InfoTooltip content="Spotify <br> Unique track identifier required for Spotify systems.
To find: Visit the artist's Spotify profile → click three dots → Share → hold Option/Alt key →
select Copy Spotify URI
"/>
            </label>
            <input
              type="text"
              id="spotify-url"
              name="spotifyUri"
              placeholder="Enter Spotify URI"
              value={values.spotifyUri || ""}
              onChange={(e) => setFieldValue("spotifyUri", e.target.value)}
              className={`w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-700 ${errors.spotifyUri && touched.spotifyUri ? 'border-red-500' : 'border-gray-300'
                }`}
            />
            <ErrorMessage name="spotifyUri" component="div" className="text-sm text-red-500" />
          </div>
        </div>
      )}

      {/* Amazon Specific Fields */}
      {isDspSelected("amazon") && (
        <div className="mt-4">
          <label className="block mb-2 text-sm font-medium text-gray-700">
            Location of Listeners <RequiredAsterisk />
            <InfoTooltip content="Amazon <br>Primary country where this artist has the strongest listener base, used for regional playlist targeting." />
          </label>
          <MultiSelect
            options={dspDropdownOptions.listenersLocation}
            value={values.listenersLocation || []}
            onChange={(value) => setFieldValue("listenersLocation", value)}
            placeholder="Select Locations"
            maxSelections={1}
            required={true}
          />

          <ErrorMessage name="listenersLocation" component="div" className="text-sm text-red-500" />
          <SimilarArtist
          />

          <div className="mt-4">
            <label className="block mb-2 text-sm font-medium text-gray-700">
              Song Category <RequiredAsterisk />
              <InfoTooltip content="Amazon <br> Identify the primary listening context or mood when audiences would engage with this track." />
            </label>
            <MultiSelect
              options={dspDropdownOptions.songCategory}
              value={values.songCategory || []}
              onChange={(value) => setFieldValue("songCategory", value)}
              placeholder="Select Categories"
              maxSelections={3}
              required={true}

            />
            <ErrorMessage name="songCategory" component="div" className="text-sm text-red-500" />
          </div>

          <div className="flex flex-col mt-4 mb-4">
            <label
              htmlFor="asin"
              className="block mb-2 text-sm font-medium text-gray-700"
            >
              ASIN
              <InfoTooltip content="Amazon <br> Amazon Standard Identification Number found in the artist's Amazon Music URL after /artists/ Example URL: amazon.com/music/player/artists/B075NQRLHS → ASIN: B075NQRLHS" />
            </label>
            <input
              type="text"
              id="asin"
              name="asin"
              placeholder="Enter ASIN"
              value={values.asin || ""}
              onChange={(e) => setFieldValue("asin", e.target.value)}
              className="w-full px-3 py-2 text-sm text-gray-700 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
      )}

      {/* Deezer Specific Fields */}
      {isDspSelected("deezer") && (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <div className="flex flex-col mt-4 mb-4">
            <label htmlFor="priority" className="block mb-2 text-sm font-medium text-gray-700">
              Priority  <RequiredAsterisk />
              <InfoTooltip content="Deezer <br> Set release priority level for editorial review. Note: Marking every release as high priority will result in all submissions being treated as regular priority" />
            </label>
            <div className="relative">
              <select
                id="priority"
                name="priority"
                value={values.priority || ""}
                onChange={(e) => setFieldValue("priority", e.target.value)}
                className={`w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-700 appearance-none pr-10 ${errors.priority && touched.priority ? 'border-red-500' : 'border-gray-300'
                  }`}
              >
                <option value="" disabled>
                  Select Priority Level
                </option>
                {dspDropdownOptions.priority.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
              <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                <span className="text-xs text-gray-400">▼</span>
              </div>
            </div>
            <ErrorMessage name="priority" component="div" className="text-sm text-red-500" />
          </div>
          {/* Fix the Deezer Editorial Team alignment by adding mt-4 */}
          <div className="flex flex-col mt-4">
            <label htmlFor="deezer-editorial" className="block mb-2 text-sm font-medium text-gray-900">
              Deezer Editorial Team <RequiredAsterisk />
              <InfoTooltip content="Deezer <br> Select the regional editorial team that should focus on this release. Choose GLOBAL for US-based campaigns." />
            </label>
            <div className="relative">
              <select
                id="deezer-editorial"
                name="deezerEditorialTeam"
                value={values.deezerEditorialTeam || ""}
                onChange={(e) => setFieldValue("deezerEditorialTeam", e.target.value)}
                className={`w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-700 appearance-none pr-10 ${errors.deezerEditorialTeam && touched.deezerEditorialTeam ? 'border-red-500' : 'border-gray-300'
                  }`}
              >
                <option value="" disabled>
                  Select Editorial Team
                </option>
                {renderOptions(dspDropdownOptions.deezerEditorialTeam)}
              </select>
              <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                <span className="text-xs text-gray-400">▼</span>
              </div>
            </div>
            <ErrorMessage name="deezerEditorialTeam" component="div" className="text-sm text-red-500" />
          </div>
        </div>
      )}

      {/* TIDAL Specific Fields */}
      {isDspSelected("TIDAL") && (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <div className="flex flex-col mb-4">
            <label htmlFor="TIDAL-page" className="block mb-2 text-sm font-medium text-gray-700">
              Link to TIDAL Artist Page  <RequiredAsterisk />
              <InfoTooltip content="TIDAL <br>Direct URL to the artist's official TIDAL profile page." />
            </label>
            <input
              type="url"
              id="TIDAL-page"
              name="TIDALpage"
              placeholder="Ex. https://tidal.com/browse/artist/17997269?u"
              value={values.TIDALpage || ""}
              onChange={(e) => setFieldValue("TIDALpage", e.target.value)}
              className={`w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-700 ${errors.TIDALpage && touched.TIDALpage ? 'border-red-500' : 'border-gray-300'
                }`}
            />
            <ErrorMessage name="TIDALpage" component="div" className="text-sm text-red-500" />
          </div>

          <div className="flex flex-col mb-4">
            <label htmlFor="artist-country" className="block mb-2 text-sm font-medium text-gray-700">
              What country do you reside in? <RequiredAsterisk />
              <InfoTooltip content="TIDAL <br>Artist's current country of residence for regional editorial consideration." />
            </label>
            <div className="relative">
              <select
                id="artist-country"
                name="artistCountry"
                value={values.artistCountry || ""}
                onChange={(e) => setFieldValue("artistCountry", e.target.value)}
                className={`w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-700 appearance-none pr-10 ${errors.artistCountry && touched.artistCountry ? 'border-red-500' : 'border-gray-300'
                  }`}
              >
                <option value="" disabled>
                  Select Country
                </option>
                {dspDropdownOptions.artistCountry.map((country) => (
                  <option key={country} value={country}>
                    {country}
                  </option>
                ))}
              </select>
              <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                <span className="text-xs text-gray-400">▼</span>
              </div>
            </div>
            <ErrorMessage name="artistCountry" component="div" className="text-sm text-red-500" />
          </div>

          <div className="flex flex-col mb-4">
            <label htmlFor="label-distributor" className="block mb-2 text-sm font-medium text-gray-700">
              Label/Distributor <RequiredAsterisk />
              <InfoTooltip content="TIDAL <br> Select your label first, then distributor if label isn't listed, or choose _Indie if neither appears in
the options.
" />
            </label>
            <div className="relative">
              <select
                id="label-distributor"
                name="LabelDistributor"
                value={values.LabelDistributor || ""}
                onChange={(e) => setFieldValue("LabelDistributor", e.target.value)}
                className={`w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-700 appearance-none pr-10 ${errors.LabelDistributor && touched.LabelDistributor ? 'border-red-500' : 'border-gray-300'
                  }`}
              >
                <option value="" disabled>
                  Select Distributor
                </option>
                {renderOptions(dspDropdownOptions.LabelDistributor)}
              </select>
              <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                <span className="text-xs text-gray-400">▼</span>
              </div>
            </div>
            <ErrorMessage name="LabelDistributor" component="div" className="text-sm text-red-500" />
          </div>

          <div className="flex flex-col mt-4 mb-4 col-span-full">
            <label htmlFor="socials-links" className="block mb-2 text-sm font-medium text-gray-700">
              Links to relevant social handles <RequiredAsterisk />
              <InfoTooltip content="TIDAL <br> Provide URLs to active social media profiles to help editorial teams understand the artist's
online presence and engagement."/>
            </label>
            <textarea
              id="socials-links"
              name="socialsLinks"
              placeholder="Instagram, TikTok, Facebook, Twitter/X, YouTube, etc..."
              value={values.socialsLinks || ""}
              onChange={(e) => setFieldValue("socialsLinks", e.target.value)}
              maxLength={500}
              className={`w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-700 h-24 ${errors.socialsLinks && touched.socialsLinks ? 'border-red-500' : 'border-gray-300'
                }`}
            />
            <ErrorMessage name="socialsLinks" component="div" className="text-sm text-red-500" />
          </div>
        </div>
      )}

      {/* SoundCloud Specific Fields */}
      {isDspSelected("soundcloud") && (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <div className="flex flex-col mt-4 mb-4">
            <label htmlFor="soundcloud-profile-url" className="block mb-2 text-sm font-medium text-gray-700">
              SoundCloud Profile URL <RequiredAsterisk />
              <InfoTooltip content="SoundCloud <br> Direct link to the artist's official SoundCloud profile." />
            </label>
            <input
              type="url"
              id="soundcloud-profile-url"
              name="soundCloudProfileUrl"
              placeholder="Enter SoundCloud profile URL"
              value={values.soundCloudProfileUrl || ""}
              onChange={(e) => setFieldValue("soundCloudProfileUrl", e.target.value)}
              className={`w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-700 ${errors.soundCloudProfileUrl && touched.soundCloudProfileUrl ? 'border-red-500' : 'border-gray-300'
                }`}
            />
            <ErrorMessage name="soundCloudProfileUrl" component="div" className="text-sm text-red-500" />
          </div>

          <div className="flex flex-col mt-4 mb-4">
            <label
              htmlFor="soundcloud-contact"
              className="block mb-2 text-sm font-medium text-gray-700"
            >
              SoundCloud Contact <RequiredAsterisk />
              <InfoTooltip content="SoundCloud <br> Select your label contact or choose Indie Label Submission if you don't have a designated
contact." />
            </label>
            <div className="relative">
              <select
                id="soundcloud-contact"
                name="soundCloudContact"
                value={values.soundCloudContact || ""}
                onChange={(e) => setFieldValue("soundCloudContact", e.target.value)}
                className={`w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-700 appearance-none pr-10 ${errors.soundCloudContact && touched.soundCloudContact ? 'border-red-500' : 'border-gray-300'
                  }`}
              >
                <option value="" disabled>
                  Select Contact
                </option>
                {renderOptions(dspDropdownOptions.soundCloudContact)}
              </select>
              <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                <span className="text-xs text-gray-400">▼</span>
              </div>
            </div>
            <ErrorMessage name="soundCloudContact" component="div" className="text-sm text-red-500" />
          </div>

          <div className="flex flex-col mt-4 mb-4">
            <label htmlFor="soundcloud-distributor" className="block mb-2 text-sm font-medium text-gray-700">
              Distributor <RequiredAsterisk />
              <InfoTooltip content="SoundCloud <br> Enter the name of the distribution service handling this release." />
            </label>
            <input
              type="text"
              id="soundcloud-distributor"
              name="distributor"
              placeholder="Enter Distributor"
              value={values.distributor || ""}
              onChange={(e) => setFieldValue("distributor", e.target.value)}
              className="w-full px-3 py-2 text-sm text-gray-700 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <ErrorMessage name="distributor" component="div" className="text-sm text-red-500" />
          </div>

          {/* <div className="flex flex-col mt-4 mb-4">
            <label htmlFor="soundcloud-release-stage" className="block mb-2 text-sm font-medium text-gray-700">
              Release Stage <RequiredAsterisk />
            </label>
            <div className="relative">
              <select
                id="soundcloud-release-stage"
                name="soundCloudReleaseStage"
                value={values.soundCloudReleaseStage || ""}
                onChange={(e) => setFieldValue("soundCloudReleaseStage", e.target.value)}
                className={`w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-700 appearance-none pr-10 ${errors.soundCloudReleaseStage && touched.soundCloudReleaseStage ? 'border-red-500' : 'border-gray-300'
                  }`}
              >
                <option value="" disabled>
                  Select Release Stage
                </option>
                {dspDropdownOptions.soundCloudReleaseStage.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
              <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                <span className="text-xs text-gray-400">▼</span>
              </div>
            </div>
            <ErrorMessage name="soundCloudReleaseStage" component="div" className="text-sm text-red-500" />
          </div> */}

          {/* Shared Release Stage field when both YouTube and SoundCloud are selected */}
          {isBothYouTubeAndSoundCloudSelected() && (
            <div className="flex flex-col mt-4 mb-4">
              <label htmlFor="shared-release-stage" className="block mb-2 text-sm font-medium text-gray-700">
                Release Stage <RequiredAsterisk />
              </label>
              <div className="relative">
                <select
                  id="shared-release-stage"
                  name="sharedReleaseStage"
                  // value={values.sharedReleaseStage || ""}
                  onChange={(e) => {
                    const value = e.target.value;
                    setFieldValue("sharedReleaseStage", value);
                    // Also set individual fields for validation
                    setFieldValue("soundCloudReleaseStage", value);
                    setFieldValue("youtubeReleaseStage", value);
                  }}
                  className={`w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-700 appearance-none pr-10 ${(errors.soundCloudReleaseStage || errors.youtubeReleaseStage) && (touched.soundCloudReleaseStage || touched.youtubeReleaseStage) ? 'border-red-500' : 'border-gray-300'}`}
                >
                  <option value="" disabled>
                    Select Release Stage
                  </option>
                  {dspDropdownOptions.soundCloudReleaseStage.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
                <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                  <span className="text-xs text-gray-400">▼</span>
                </div>
              </div>
              <ErrorMessage name="soundCloudReleaseStage" component="div" className="text-sm text-red-500" />
              <ErrorMessage name="youtubeReleaseStage" component="div" className="text-sm text-red-500" />
            </div>
          )}

          {/* SoundCloud Release Stage - only show when SoundCloud is selected but YouTube is not */}
          {isDspSelected("soundcloud") && !isDspSelected("youtube") && (
            <div className="flex flex-col mt-4 mb-4">
              <label htmlFor="soundcloud-release-stage" className="block mb-2 text-sm font-medium text-gray-700">
                Release Stage <RequiredAsterisk />
              </label>
              <div className="relative">
                <select
                  id="soundcloud-release-stage"
                  name="soundCloudReleaseStage"
                  value={values.soundCloudReleaseStage || ""}
                  onChange={(e) => setFieldValue("soundCloudReleaseStage", e.target.value)}
                  className={`w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-700 appearance-none pr-10 ${errors.soundCloudReleaseStage && touched.soundCloudReleaseStage ? 'border-red-500' : 'border-gray-300'}`}
                >
                  <option value="" disabled>
                    Select Release Stage
                  </option>
                  {dspDropdownOptions.soundCloudReleaseStage.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
                <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                  <span className="text-xs text-gray-400">▼</span>
                </div>
              </div>
              <ErrorMessage name="soundCloudReleaseStage" component="div" className="text-sm text-red-500" />
            </div>
          )}

          <div className="mt-4">
            <label
              className="block mb-2 text-sm font-medium text-gray-700"
              htmlFor="promotion-territories"
            >
              Promotion Territories <RequiredAsterisk />
              <InfoTooltip content="SoundCloud <br> Select the primary geographic region for promotional focus from the available territory options" />
            </label>
            <MultiSelect
              options={dspDropdownOptions.promotionTerritories}
              value={values.promotionTerritories || []}
              onChange={(value) => setFieldValue("promotionTerritories", value)}
              placeholder="Select promotion territories"
              required={true}
            />
            <ErrorMessage name="promotionTerritories" component="div" className="text-sm text-red-500" />
          </div>

          <div className="mt-4">
            <label
              className="block mb-2 text-sm font-medium text-gray-700"
              htmlFor="fresh-music-friday"
            >
              Submit for Fresh Music Friday <RequiredAsterisk />
              <InfoTooltip content="SoundCloud <br>  Choose which country's Fresh Music Friday playlist you'd like to be considered for." />
            </label>
            <MultiSelect
              options={dspDropdownOptions.freshMusicFriday}
              value={values.freshMusicFriday || []}
              onChange={(value) => setFieldValue("freshMusicFriday", value)}
              placeholder="Select Fresh Music Friday options"
              required={true}
            />
            <ErrorMessage name="freshMusicFriday" component="div" className="text-sm text-red-500" />
          </div>

          <div className="flex flex-col mt-4 mb-4">
            <label
              htmlFor="playlist-consideration"
              className="block mb-2 text-sm font-medium text-gray-700"
            >
              Playlist Consideration <RequiredAsterisk />
              <InfoTooltip content="SoundCloud <br> Select the SoundCloud editorial playlist that best matches your song's genre and style for
targeted consideration." />
            </label>
            <div className="relative">
              <select
                id="playlist-consideration"
                name="playlistConsideration"
                value={values.playlistConsideration || ""}
                onChange={(e) => setFieldValue("playlistConsideration", e.target.value)}
                className={`w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-700 appearance-none pr-10 ${errors.playlistConsideration && touched.playlistConsideration ? 'border-red-500' : 'border-gray-300'
                  }`}
              >
                <option value="" disabled>
                  Select Playlist
                </option>
                {dspDropdownOptions.playlistConsideration.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
              <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                <span className="text-xs text-gray-400">▼</span>
              </div>
            </div>
            <ErrorMessage name="playlistConsideration" component="div" className="text-sm text-red-500" />
          </div>
        </div>
      )}

      {/* Apple Specific Fields */}
      {isDspSelected("apple") && (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <div className="flex flex-col mt-4 mb-4">
            <label htmlFor="apple-id" className="block mb-2 text-sm font-medium text-gray-700">
              Apple Artist ID
            </label>
            <input
              type="number"
              id="apple-id"
              name="appleId"
              placeholder="Enter  Apple Artist ID"
              value={values.appleId || ""}
              onChange={(e) => setFieldValue("appleId", e.target.value)}
              className={`w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-700 ${errors.appleId && touched.appleId ? 'border-red-500' : 'border-gray-300'
                }`}
            />
            <ErrorMessage name="appleId" component="div" className="text-sm text-red-500" />
          </div>

          <div className="flex flex-col mt-4 mb-4">
            <label htmlFor="apple-id" className="block mb-2 text-sm font-medium text-gray-700">
              Apple Track ID
            </label>
            <input
              type="number"
              id="apple-track-id"
              name="appleTrackId"
              placeholder="Enter Apple Track ID"
              value={values.appleTrackId || ""}
              onChange={(e) => setFieldValue("appleTrackId", e.target.value)}
              className={`w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-700 ${errors.appleId && touched.appleId ? 'border-red-500' : 'border-gray-300'
                }`}
            />
            <ErrorMessage name="appleTrackId" component="div" className="text-sm text-red-500" />
          </div>

        </div>
      )}

      {/* YouTube Specific Fields */}
      {isDspSelected("youtube") && (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <div className="mt-4">
            <label
              className="block mb-2 text-sm font-medium text-gray-700"
              htmlFor="youtube-promotion-surface"
            >
              Promotion Surface <RequiredAsterisk />
            </label>
            <MultiSelect
              options={dspDropdownOptions.youtubePromotionSurface}
              value={values.youtubePromotionSurface || []}
              onChange={(value) => setFieldValue("youtubePromotionSurface", value)}
              placeholder="Select promotion surfaces"
              required={true}
            />
            <ErrorMessage name="youtubePromotionSurface" component="div" className="text-sm text-red-500" />
          </div>

          {isDspSelected("youtube") && !isDspSelected("soundcloud") && (
            <div className="flex flex-col mt-4 mb-4">
              <label htmlFor="youtube-release-stage" className="block mb-2 text-sm font-medium text-gray-700">
                Release Stage <RequiredAsterisk />
              </label>
              <div className="relative">
                <select
                  id="youtube-release-stage"
                  name="youtubeReleaseStage"
                  value={values.youtubeReleaseStage || ""}
                  onChange={(e) => setFieldValue("youtubeReleaseStage", e.target.value)}
                  className={`w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-700 appearance-none pr-10 ${errors.youtubeReleaseStage && touched.youtubeReleaseStage ? 'border-red-500' : 'border-gray-300'}`}
                >
                  <option value="" disabled>
                    Select Release Stage
                  </option>
                  {dspDropdownOptions.youtubeReleaseStage.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
                <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                  <span className="text-xs text-gray-400">▼</span>
                </div>
              </div>
              <ErrorMessage name="youtubeReleaseStage" component="div" className="text-sm text-red-500" />
            </div>
          )}


          <div className="flex flex-col mt-4 mb-4">
            <label
              htmlFor="youtube-promotion-date"
              className="block mb-2 text-sm font-medium text-gray-700"
            >
              Promotion Date <RequiredAsterisk />
            </label>
            <input
              type="date"
              id="youtube-promotion-date"
              name="youtubePromotionDate"
              value={values.youtubePromotionDate || ""}
              onChange={(e) => setFieldValue("youtubePromotionDate", e.target.value)}
              min={new Date().toISOString().split('T')[0]}
              className={`w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-700 ${errors.youtubePromotionDate && touched.youtubePromotionDate ? 'border-red-500' : 'border-gray-300'
                }`}
            />
            <ErrorMessage name="youtubePromotionDate" component="div" className="text-sm text-red-500" />
          </div>

          <div className="mt-4">
            <label
              className="block mb-2 text-sm font-medium text-gray-700"
              htmlFor="youtube-territories"
            >
              Territories <RequiredAsterisk />
            </label>
            <MultiSelect
              options={dspDropdownOptions.youtubeTerritories}
              value={values.youtubeTerritories || []}
              onChange={(value) => setFieldValue("youtubeTerritories", value)}
              placeholder="Select territories (max 3)"
              maxSelections={3}
              required={true}
            />
            <ErrorMessage name="youtubeTerritories" component="div" className="text-sm text-red-500" />
          </div>
        </div>
      )}
      {/* Special file upload section for Amazon voice intro */}
      {isDspSelected("amazon") && values.addAmazonVoiceIntro && (
        <div className="flex flex-col items-start justify-between w-full gap-5 mt-5 lg:flex-row">
          <div className="flex-1 p-5 text-center rounded-lg bg-gray-50">
            <h4 className="mb-4 font-bold">Upload Voice Intro</h4>
            <input
              type="file"
              name="voice-intro"
              onChange={(e) => handleFileInputChange(e, "voiceIntro")}
              accept="audio/*"
              className="w-[90%] mx-auto p-2 border border-dashed border-gray-300 rounded cursor-pointer"
            />
            <p className="text-xs text-gray-500 mt-2.5">Max. file size: 2 GB. Max 15 seconds.</p>
          </div>

          <div className="flex-1 p-5 text-center rounded-lg bg-gray-50">
            <h4 className="mb-4 font-bold">Upload Intro Image</h4>
            <input
              type="file"
              name="intro-image"
              onChange={(e) => handleFileInputChange(e, "introImage")}
              accept="image/*"
              className="w-[90%] mx-auto p-2 border border-dashed border-gray-300 rounded cursor-pointer"
            />
            <p className="text-xs text-gray-500 mt-2.5">Max. file size: 2 GB. Min. 1100x1100px.</p>
          </div>
        </div>
      )}


    </section>
  );
};

export default DspSpecificInfo;